import { Injectable, NgZone } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { BehaviorSubject, fromEvent, merge, Subject, Subscription, timer } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class IdleService {
  private idleTimeout = 10 * 60 * 1000; // 10 min
  private warningDuration = 60 * 1000;  // 1 min before logout
  
  private activityEvents$ = merge(
    fromEvent(document, 'mousemove'),
    fromEvent(document, 'keydown'),
    fromEvent(document, 'click'),
    fromEvent(document, 'scroll'),
    fromEvent(document, 'touchstart')
  );

  private timerSub?: Subscription;
  private warningTimerSub?: Subscription;

  onTimeoutWarning$ = new Subject<number>();
  onTimeout$ = new Subject<void>();

  constructor(private auth: AuthService, private ngZone: NgZone) {
    this.resetTimer();
    this.initListeners();

    // 👇 Subscribe once, handle logout
    this.onTimeout$.subscribe(() => {
      console.log('Idle timeout reached, logging out...');
      this.auth.logout({ logoutParams: { returnTo: window.location.origin } });
    });
  }

  private initListeners() {
    // fallback in case merge misses an event
    ['click', 'mousemove', 'keypress', 'scroll', 'keydown', 'touchstart'].forEach(event => {
      window.addEventListener(event, () => this.resetTimer());
    });
  }

  startWatching() {
    this.ngZone.runOutsideAngular(() => {
      this.resetTimer();
      this.activityEvents$.subscribe(() => this.resetTimer());
    });
  }

  resetTimer() {
    // Clear existing timers
    this.timerSub?.unsubscribe();
    this.warningTimerSub?.unsubscribe();
    this.onTimeoutWarning$.next(0);

    // Restart main idle timer
    this.timerSub = timer(this.idleTimeout - this.warningDuration).subscribe(() => {
      // Show warning
      let remaining = this.warningDuration / 1000;
      this.ngZone.run(() => this.onTimeoutWarning$.next(remaining));

      this.warningTimerSub = timer(0, 1000).subscribe(() => {
        remaining--;
        this.ngZone.run(() => this.onTimeoutWarning$.next(remaining));

        if (remaining <= 0) {
          this.warningTimerSub?.unsubscribe();
          this.ngZone.run(() => this.onTimeout$.next());
        }
      });
    });
  }
}